import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { api } from '../api/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 480px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #24BA7D;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Icon = styled.div`
  color: white;
  font-weight: bold;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const ModalMessage = styled.p`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #838D90;
  margin: 0;
  line-height: 1.5;
`;

const InputLabel = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const BuyerSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button<{ $isOpen: boolean; $hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.$isOpen ? '#24BA7D' : '#284640'};
  border-radius: 8px;
  background-color: #172A2A;
  color: ${props => props.$hasValue ? '#FFFFFF' : '#838D90'};
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownArrow = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: #838D90;
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #0E1F1F;
  border: 1px solid #24BA7D;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const DropdownOption = styled.div<{ isNew?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  color: ${props => props.isNew ? '#24BA7D' : '#FFFFFF'};
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  font-weight: ${props => props.isNew ? '500' : '400'};
  border-bottom: 1px solid #284640;
  
  &:hover {
    background-color: #172A2A;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  &::placeholder {
    color: #838D90;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary'; disabled?: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
  
  ${props => props.variant === 'primary' ? `
    background-color: #24BA7D;
    color: #FFFFFF;
    
    &:hover:not(:disabled) {
      background-color: #1E9A66;
    }
  ` : `
    background-color: transparent;
    color: #838D90;
    border: 1px solid #284640;
    
    &:hover:not(:disabled) {
      background-color: #172A2A;
      color: #FFFFFF;
    }
  `}
`;

const SmallButton = styled.button`
  background-color: transparent;
  color: #838D90;
  border: 1px solid #284640;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  
  &:hover:not(:disabled) {
    background-color: #172A2A;
    color: #FFFFFF;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface BuyerModalProps {
  isOpen: boolean;
  onConfirm: (buyer: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const BuyerModal: React.FC<BuyerModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [buyer, setBuyer] = useState('');
  const [buyers, setBuyers] = useState<{name: string}[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingBuyers, setIsLoadingBuyers] = useState(false);
  const [useNewBuyer, setUseNewBuyer] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Загружаем список покупателей при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      loadBuyers();
    }
  }, [isOpen]);

  // Закрываем dropdown при клике вне его
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const loadBuyers = async () => {
    setIsLoadingBuyers(true);
    try {
      const response = await api.getBuyers();
      if (response && Array.isArray(response)) {
        setBuyers(response);
      }
    } catch (error) {
      console.error('Ошибка при загрузке списка покупателей:', error);
      setBuyers([]);
    } finally {
      setIsLoadingBuyers(false);
    }
  };

  const handleConfirm = () => {
    if (buyer.trim()) {
      onConfirm(buyer.trim());
      setBuyer('');
      setUseNewBuyer(false);
      setIsDropdownOpen(false);
    }
  };

  const handleCancel = () => {
    setBuyer('');
    setUseNewBuyer(false);
    setIsDropdownOpen(false);
    onCancel();
  };

  const handleSelectBuyer = (selectedBuyer: string) => {
    setBuyer(selectedBuyer);
    setIsDropdownOpen(false);
    setUseNewBuyer(false);
  };

  const handleSelectNewBuyer = () => {
    setBuyer('');
    setUseNewBuyer(true);
    setIsDropdownOpen(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && buyer.trim() && !isLoading) {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <IconContainer>
            <Icon>₽</Icon>
          </IconContainer>
          <ModalTitle>Информация о продаже</ModalTitle>
        </ModalHeader>
        
        <ModalMessage>
          Укажите имя покупателя для отметки товара как проданного
        </ModalMessage>
        
        <BuyerSelectorContainer>
          <InputLabel>Имя покупателя</InputLabel>
          
          {!useNewBuyer ? (
            <DropdownContainer ref={dropdownRef}>
              <DropdownButton
                type="button"
                $isOpen={isDropdownOpen}
                $hasValue={!!buyer}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={isLoading || isLoadingBuyers}
              >
                {isLoadingBuyers ? 'Загрузка...' : buyer || 'Выберите покупателя или введите нового'}
                <DropdownArrow $isOpen={isDropdownOpen}>
                  ▼
                </DropdownArrow>
              </DropdownButton>
              
              <DropdownList $isOpen={isDropdownOpen}>
                {buyers.length > 0 && buyers.map((buyerItem, index) => (
                  <DropdownOption
                    key={index}
                    onClick={() => handleSelectBuyer(buyerItem.name)}
                  >
                    {buyerItem.name}
                  </DropdownOption>
                ))}
                <DropdownOption
                  isNew
                  onClick={handleSelectNewBuyer}
                >
                  + Добавить нового покупателя
                </DropdownOption>
              </DropdownList>
            </DropdownContainer>
          ) : (
            <Input
              type="text"
              placeholder="Введите имя нового покупателя"
              value={buyer}
              onChange={(e) => setBuyer(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              autoFocus
            />
          )}
          
          {useNewBuyer && (
            <SmallButton
              onClick={() => {
                setUseNewBuyer(false);
                setBuyer('');
              }}
              disabled={isLoading}
            >
              ← Вернуться к списку
            </SmallButton>
          )}
        </BuyerSelectorContainer>
        
        <ButtonContainer>
          <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirm} 
            disabled={!buyer.trim() || isLoading}
          >
            {isLoading ? 'Сохранение...' : 'Продать'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default BuyerModal; 